import { Badge, BadgeProps } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export type StatusType = "LIVE" | "REMOVED" | "BLOCKED" | "DOMAIN_DOWN" | "DOMAIN_SEIZED" | "ACTIVE" | "INACTIVE" | "pending" | "approved" | "sent" | string | null | undefined;

export interface StatusBadgeProps extends Omit<BadgeProps, "variant"> {
  status: StatusType;
  /**
   * Custom label to display instead of the status value
   */
  label?: string;
  /**
   * Children to display (overrides both label and formatted status)
   */
  children?: React.ReactNode;
}

/**
 * StatusBadge component for displaying status with consistent styling.
 * Uses theme tokens for colors that adapt to light/dark mode.
 */
export function StatusBadge({ status, label, children, className, ...props }: StatusBadgeProps) {
  const normalizedStatus = status?.trim().toUpperCase() || "";
  
  // Determine status classes based on status
  let statusClasses = "";
  
  if (normalizedStatus === "LIVE") {
    statusClasses = "status-live";
  } else if (normalizedStatus === "REMOVED") {
    statusClasses = "status-removed";
  } else if (normalizedStatus === "BLOCKED" || normalizedStatus === "DOMAIN_DOWN" || normalizedStatus === "DOMAIN_SEIZED") {
    statusClasses = "status-blocked";
  } else if (normalizedStatus === "ACTIVE") {
    statusClasses = "status-active";
  } else if (normalizedStatus === "INACTIVE") {
    statusClasses = "";
  } else if (normalizedStatus === "PENDING") {
    statusClasses = "bg-yellow-100 text-yellow-800 border-yellow-200";
  } else if (normalizedStatus === "APPROVED") {
    statusClasses = "bg-blue-100 text-blue-800 border-blue-200";
  } else if (normalizedStatus === "SENT") {
    statusClasses = "bg-green-100 text-green-800 border-green-200";
  }
  
  // Format display text - children takes priority, then label, then formatted status
  const displayText = children || label || formatStatusText(status);
  
  return (
    <Badge
      variant="outline"
      className={cn(
        "px-1.5 py-0.5 text-xs font-medium rounded-lg",
        statusClasses,
        className
      )}
      {...props}
    >
      {displayText}
    </Badge>
  );
}

/**
 * Format status text for display (capitalize first letter, lowercase rest)
 */
function formatStatusText(status: StatusType): string {
  if (!status) return "Unknown";
  const normalized = status.trim();
  return normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase();
}

